import feathersApp from '../feathers';

import { loadMatches } from '@actions/matches';
import { loadTeams } from '@actions/teams';
import { loadPlayers } from '@actions/players';
import { loadForecast, loadForecastUser } from '@actions/forecast';
import { loadBracketView } from '@actions/bracketView';
import { loadScores } from '@actions/scores';
//import { loadAuthUser } from "@actions/auth";
import {
  loadMyRankings,
  loadTop,
  loadMyRange,
  loadInvite,
  INVITE_LOCALSTORAGE_KEY,
} from '@actions/ranking';
import { loadRanking } from '@pages/RankingDetails';
import { isWordCupStarted } from '@selectors/clock';
import { loading } from '@actions';
import { redirect } from 'redux-first-router';
import { actions as Pages } from '@pages/names';

import { isServer } from '@utils/browser';
import { isCleanExit } from '@selectors/matches';
import * as analytics from '@analytics';
import get from 'lodash/get';

const isAuth = (state) => !!state.jwToken;
const hasProfileSet = (state) =>
  !!(state.user && state.user.username && state.user.dni);

const checkAuth = async (dispatch, loginRedirect = true) => {
  let accessToken;
  try {
    accessToken = await feathersApp.authentication.getAccessToken();
  } catch (error) {
    accessToken = null;
  }

  if (!accessToken) {
    if (loginRedirect) {
      dispatch(redirect({ type: Pages.Login }));
    }
    return;
  }

  dispatch({ type: 'AUTH_LOADING', payload: true });
  try {
    // force: true — siempre consulta al servidor (sync contributor/comodines en reAuthenticate)
    const result = await feathersApp.authentication.reAuthenticate(true);

    if (result.accessToken) {
      dispatch({ type: 'AUTH_SET_TOKEN', payload: result.accessToken });
      dispatch({ type: 'AUTH_SET_USER', payload: result.user });
      analytics.set({ userId: result.user._id });
    }
  } catch (e) {
    try {
      await feathersApp.authentication.logout();
    } catch (error) {
      console.error(error);
    }
    dispatch({ type: 'AUTH_CLEAR' });
    if (loginRedirect) {
      dispatch(redirect({ type: Pages.Login }));
    }
  } finally {
    dispatch({ type: 'AUTH_LOADING', payload: false });
  }
};

const checkProfile = async (dispatch, getState) => {
  if (!hasProfileSet(getState())) {
    dispatch(redirect({ type: Pages.Signup }));
  }
};

const scrollTop = () => {
  try {
    const switcher = document.querySelector('.switcher > div');
    if (switcher) {
      switcher.scrollTo(0, 0);
    }
  } catch (error) {
    console.error(error);
  }
};

const matchesThunk = async (dispatch, getState) => {
  scrollTop();
  await checkAuth(dispatch);
  await checkProfile(dispatch, getState);
  await Promise.all([dispatch(loadMatches()), dispatch(loadTeams())]);

  if (isAuth(getState())) {
    await Promise.all([
      // dispatch(loadAuthUser(isServer)),
      isWordCupStarted(getState())
        ? Promise.resolve()
        : dispatch(loadPlayers()), // Don't fetch players if they cannot be editable
      dispatch(loadForecast()),
    ]);

    dispatch(loadBracketView());

    if (!isWordCupStarted(getState())) {
      dispatch({ type: 'SUBPAGE_FORECAST' });
    }
  }
};

const snapshotThunk = async (dispatch, getState) => {
  await checkAuth(dispatch);

  if (!isAuth(getState())) {
    dispatch(redirect({ type: Pages.Home }));
    return;
  }

  if (isWordCupStarted(getState())) {
    await Promise.all([
      dispatch(loadMatches()),
      dispatch(loadTeams()),
    ]);
    await dispatch(loadForecast());
    await dispatch(loadBracketView());
  }
};

const rankingThunk = async (dispatch, getState) => {
  if (isWordCupStarted(getState())) {
    dispatch(loading(true));

    await dispatch(loadTop());

    await checkAuth(dispatch);

    if (isAuth(getState())) {
      // await dispatch(loadAuthUser(isServer));
      await dispatch(loadMyRange());
    }

    dispatch(loading(false));
  }
};

const myprofileThunk = async (dispatch, getState) => {
  await checkAuth(dispatch);

  if (!isAuth(getState())) {
    dispatch(redirect({ type: Pages.Home }));
    return;
  }

  // await dispatch(loadAuthUser(isServer));
  await dispatch(loadMyRankings());
};

const rankingInviteThunk = async (dispatch, getState) => {
  const {
    location: {
      payload: { inviteCode },
    },
  } = getState();

  dispatch(loading(true));
  await checkAuth(dispatch, false);

  await dispatch(loadInvite(inviteCode));

  if (isAuth(getState()) && !hasProfileSet(getState())) {
    dispatch(redirect({ type: Pages.Signup }));
    dispatch(loading(false));
    return;
  }

  dispatch(loading(false));
};

const rankingDetailsThunk = async (dispatch, getState) => {
  const {
    location: {
      payload: { id },
    },
  } = getState();

  dispatch({ type: 'RANKING_DETAILS_REQUESTED', payload: id });
  dispatch(loading(true));
  if (!isAuth(getState())) {
    await checkAuth(dispatch, false);
  }

  try {
    await dispatch(loadRanking(id));
  } catch (e) {
    if (e.response.status === 404) {
      dispatch({ type: 'RANKING_NOT_FOUND' });
    }
    throw e;
  } finally {
    dispatch(loading(false));
  }
};

const snapshotsThunk = async (dispatch, getState) => {
  scrollTop();
  const {
    location: {
      payload: { userId },
    },
  } = getState();

  dispatch(loading(true));
  await checkAuth(dispatch, false);

  await Promise.all([
    dispatch(loadForecastUser(userId)),
    dispatch(loadMatches()),
    dispatch(loadTeams()),
  ]);

  await Promise.all([
    dispatch(loadForecast(userId)),
    dispatch(loadBracketView(userId)),
  ]);

  dispatch({ type: 'SUBPAGE_FORECAST' });
  dispatch(loading(false));
};

const scoresThunk = async (dispatch, getState) => {
  const userIdParam = get(getState(), 'location.payload.userId');

  await checkAuth(dispatch, !userIdParam);

  const userId = userIdParam ? userIdParam : get(getState(), 'user._id', null);

  dispatch(loading(true));

  await Promise.all([
    dispatch(loadForecastUser(userId)),
    dispatch(loadMatches()),
    dispatch(loadTeams()),
  ]);

  await dispatch(loadScores(userId));

  dispatch(loading(false));
};

const fixtureThunk = async (dispatch, getState) => {
  await checkAuth(dispatch, false);

  dispatch(loading(true));

  // const userId = get(getState(), 'user._id', '');

  await Promise.all([
    dispatch(loadMatches()),
    dispatch(loadTeams()),
    // ...(userId
    //   ? [
    //       dispatch(loadForecastUser(userId)),
    //       dispatch(loadForecast(userId)),
    //       dispatch(loadSnapshot(userId)),
    //       dispatch(loadScores(userId)),
    //     ]
    //   : []),
  ]);

  dispatch(loading(false));
};

const rulesThunk = async (dispatch) => {
  scrollTop();
  await checkAuth(dispatch, false);
};

const loginThunk = async (dispatch, getState) => {
  // OAuth hash se procesa en src/index.js al cargar la app.
  await checkAuth(dispatch, false);

  // Si ya está autenticado, redirigir según perfil.
  if (isAuth(getState())) {
    if (!hasProfileSet(getState())) {
      dispatch(
        redirect({
          type: Pages.Signup,
        })
      );
      return;
    }

    const previousInviteCode = localStorage.getItem(INVITE_LOCALSTORAGE_KEY);
    if (previousInviteCode) {
      dispatch(
        redirect({
          type: Pages.RankingInvite,
          payload: { inviteCode: previousInviteCode },
        })
      );
      return;
    }

    dispatch(
      redirect({
        type: Pages.Home,
      })
    );
  }
};

const signUpThunk = async (dispatch, getState) => {
  await checkAuth(dispatch);
  if (hasProfileSet(getState())) {
    dispatch(redirect({ type: Pages.Home }));
  }
};

const routes = {
  [Pages.Home]: {
    path: '/',
    thunk: matchesThunk,
  },
  [Pages.Matches]: {
    path: '/partidos',
    thunk: matchesThunk,
    confirmLeave: (state, action) => {
      if (!isServer && !isCleanExit(state)) {
        return 'Su pronóstico tiene cambios sin guardar, continuar?';
      }
    },
  },
  [Pages.Snapshot]: {
    path: '/mi-mundialito',
    thunk: snapshotThunk,
  },
  [Pages.Ranking]: {
    path: '/ranking',
    thunk: rankingThunk,
  },
  [Pages.MyProfile]: {
    path: '/myprofile',
    thunk: myprofileThunk,
  },
  [Pages.MyRankings]: {
    path: '/mis-torneos',
    thunk: myprofileThunk,
  },
  [Pages.RankingInvite]: {
    path: '/invitacion/:inviteCode',
    thunk: rankingInviteThunk,
  },
  [Pages.RankingDetails]: {
    path: '/rankings/:id',
    thunk: rankingDetailsThunk,
  },
  [Pages.Snapshots]: {
    path: '/mundial-ideal/:userId',
    thunk: snapshotsThunk,
  },
  [Pages.Scores]: {
    path: '/puntajes/:userId?',
    thunk: scoresThunk,
  },
  [Pages.Login]: {
    path: '/acceder',
    thunk: loginThunk,
  },
  [Pages.Signup]: {
    path: '/registro',
    thunk: signUpThunk,
  },
  [Pages.Fixture]: {
    path: '/fixture',
    thunk: fixtureThunk,
  },
  [Pages.FixtureEmbed]: {
    path: '/fixture-embed/:round?',
    thunk: fixtureThunk,
  },
  [Pages.Rules]: {
    path: '/reglas',
    thunk: rulesThunk,
  },
  [Pages.TermsAndConditions]: {
    path: '/terms-and-conditions',
    thunk: rulesThunk,
  },
};

export default routes;
